import React, { useState, useRef } from 'react';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import { ReviewCaveat } from '../components/ReviewAid';
import { parseSnapchatReturn } from '../utils/snapchatWarrant';
import SnapchatReport, { buildMediaMap } from '../components/snapchat/Report';

// ─── Main component ───────────────────────────────────────────────────────────
function SnapchatWarrantProcessor() {
  const [phase, setPhase]       = useState('upload');
  const [data, setData]         = useState(null);
  const [error, setError]       = useState('');
  const [files, setFiles]       = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [mediaMap, setMediaMap] = useState({});

  const fileInputRef  = useRef(null);
  const mediaInputRef = useRef(null);

  function handleFiles(fileList) {
    const csvFiles = Array.from(fileList).filter(f => f.name.toLowerCase().endsWith('.csv'));
    if (!csvFiles.length) { setError('No CSV files found. Select the CSV files from the warrant return.'); return; }
    setFiles(fileList);
    setError('');
  }

  async function handleParse() {
    trackToolAction('/snapchatwarrant', 'Snapchat Warrant Processor');
    if (!files) return;
    setPhase('parsing');
    setError('');
    try {
      const fileMap = {};
      await Promise.all(Array.from(files).map(f => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => { fileMap[f.name.toLowerCase()] = e.target.result; resolve(); };
        reader.onerror = () => reject(new Error(`Failed to read ${f.name}`));
        reader.readAsText(f, 'utf-8');
      })));
      const parsed = parseSnapchatReturn(fileMap);
      setData(parsed);
      setPhase('report');
    } catch (err) {
      setError('Error parsing files: ' + err.message);
      setPhase('upload');
    }
  }

  function handleNewFile() { setPhase('upload'); setFiles(null); setData(null); setError(''); setMediaMap({}); }

  // ── Upload ─────────────────────────────────────────────────────────────────
  if (phase !== 'report') {
    const csvCount = files ? Array.from(files).filter(f => f.name.toLowerCase().endsWith('.csv')).length : 0;
    return (
      <div style={{ minHeight: '100vh', background: '#f0f4f8', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
        <Navbar pageTitle="Snapchat Warrant Processor" />
        <div style={{ maxWidth: 620, margin: '0 auto', padding: '48px 24px 80px' }}>
          <div style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px', margin: 0 }}>Snapchat Warrant Processor</h2>
            <p style={{ color: '#64748b', marginTop: 6, fontSize: 15, lineHeight: 1.55 }}>
              Select all <strong>CSV files</strong> from a Snapchat search warrant return to browse account info,
              conversations, IP addresses, friends, memories, and all data sections.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 24, padding: '13px 18px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, fontSize: 14, color: '#166534', lineHeight: 1.5 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <span><strong>100% local &amp; private.</strong> All files are parsed in your browser. Nothing is sent to any server.</span>
          </div>
          <ReviewCaveat what="the actual returns from Snap (Snapchat)" style={{ marginBottom: 24 }} />
          <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)', padding: 32 }}>
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files); }}
              style={{ border: `2px dashed ${dragOver ? '#f59e0b' : '#fde68a'}`, borderRadius: 10, padding: '40px 24px', textAlign: 'center', cursor: 'pointer', background: dragOver ? '#fffbeb' : 'transparent', transition: 'border-color .2s, background .2s' }}
            >
              <input ref={fileInputRef} type="file" accept=".csv" multiple style={{ display: 'none' }} onChange={e => { if (e.target.files.length) handleFiles(e.target.files); }} />
              <div style={{ width: 48, height: 48, borderRadius: 12, background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', color: '#f59e0b' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>Drop CSV files here</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>or <span style={{ color: '#f59e0b', fontWeight: 600 }}>click to browse</span> — select all CSVs from the warrant return folder</div>
            </div>

            {files && csvCount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, padding: '12px 16px', background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#92400e' }}>{csvCount} CSV file{csvCount !== 1 ? 's' : ''} selected</span>
              </div>
            )}

            <div style={{ marginTop: 16, padding: '12px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>Optional: Load media folder</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10, lineHeight: 1.5 }}>
                Select the warrant return folder to display the profile photo and memory snapshots inline.
              </div>
              <input ref={mediaInputRef} type="file" multiple webkitdirectory="" style={{ display: 'none' }} onChange={e => { if (e.target.files.length) setMediaMap(buildMediaMap(e.target.files)); }} />
              <button onClick={() => mediaInputRef.current?.click()} style={{ fontSize: 12, padding: '6px 14px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', cursor: 'pointer', fontWeight: 600 }}>
                {Object.keys(mediaMap).length > 0 ? `✓ ${Math.round(Object.keys(mediaMap).length / 2)} media files loaded` : 'Select media folder…'}
              </button>
            </div>

            <button onClick={handleParse} disabled={!files || csvCount === 0 || phase === 'parsing'} style={{ display: 'block', width: '100%', marginTop: 20, padding: 13, background: '#f59e0b', color: '#1c1400', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: (!files || csvCount === 0 || phase === 'parsing') ? 'not-allowed' : 'pointer', opacity: (!files || csvCount === 0 || phase === 'parsing') ? 0.55 : 1 }}>
              {phase === 'parsing' ? 'Parsing…' : 'Parse Warrant Return'}
            </button>

            {error && <div style={{ marginTop: 16, padding: '12px 16px', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 8, fontSize: 14 }}>{error}</div>}
          </div>
        </div>
      </div>
    );
  }

  return <SnapchatReport data={data} mediaMap={mediaMap} onLoadMedia={files => setMediaMap(buildMediaMap(files))} onReset={handleNewFile} />;
}

export default SnapchatWarrantProcessor;
